import React, { useState } from "react";
import { MdEmail, MdLocationOn, MdPhone, MdAccessTime } from "react-icons/md";
import toast from "react-hot-toast";
import { submitContact, getApiErrorMessage } from "../../config/api";
import { normalizeEmail, isValidEmailFormat } from "../../utils/emailUtils";

const ContactSection = () => {
  const [isSending, setIsSending] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "",
    message: "",
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const sendEmail = async (e) => {
    e.preventDefault();
    const email = normalizeEmail(formData.email);

    if (!formData.name.trim()) {
      toast.error("Please enter your name");
      return;
    }

    if (!isValidEmailFormat(email)) {
      toast.error("Please enter a valid email address");
      return;
    }

    if (!formData.message.trim() || formData.message.trim().length < 10) {
      toast.error("Message must be at least 10 characters");
      return;
    }

    setIsSending(true);

    try {
      await submitContact({
        name: formData.name.trim(),
        email,
        subject: formData.subject.trim() || "Website inquiry",
        message: formData.message.trim(),
      });
      toast.success("Message sent successfully!");
      setFormData({ name: "", email: "", subject: "", message: "" });
    } catch (error) {
      toast.error(
        getApiErrorMessage(error, "Failed to send the message. Please try again."),
      );
    } finally {
      setIsSending(false);
    }
  };

  return (
    <section className="max-w-7xl mx-auto py-12 px-6 sm:px-8">
      <div className="text-center mb-12">
        <h2 className="text-3xl sm:text-4xl text-primary-600 font-extrabold text-gray-900">
          Get In Touch With Us
        </h2>
        <p className="mt-4 text-lg text-gray-700 max-w-2xl mx-auto">
          For more information about our products and services, feel free to
          drop us an email. Our team is always here to help. Do not hesitate!
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
        {/* Contact Information */}
        <div className="space-y-8">
          <div className="flex items-start">
            <MdLocationOn size={30} className="text-primary-600 mt-1" />
            <div className="ml-4">
              <h4 className="text-xl font-semibold text-gray-900">Address</h4>
              <p className="mt-2 text-gray-700">
                <span className="font-semibold">Knots & Krafts</span>
                <br />
                302, JSJ Opal Apartments, BHEL Officers Layout, Jayanagar,
                Bengaluru, Karnataka 560041, India
              </p>
            </div>
          </div>
          <div className="flex items-start">
            <MdPhone size={30} className="text-primary-600 mt-1" />
            <div className="ml-4">
              <h4 className="text-xl font-semibold text-gray-900">Phone</h4>
              <p className="mt-2 text-gray-700">+91 94804 24751</p>
            </div>
          </div>
          <div className="flex items-start">
            <MdEmail size={30} className="text-primary-600 mt-1" />
            <div className="ml-4">
              <h4 className="text-xl font-semibold text-gray-900">Email</h4>
              <p className="mt-2 text-gray-700">
                <a
                  href="mailto:knots.krafts@gmail.com"
                  className="hover:underline"
                >
                  knots.krafts@gmail.com
                </a>
              </p>
            </div>
          </div>
          <div className="flex items-start">
            <MdAccessTime size={30} className="text-primary-600 mt-1" />
            <div className="ml-4">
              <h4 className="text-xl font-semibold text-gray-900">
                Business Hours
              </h4>
              <p className="mt-2 text-gray-700">
                <strong>Monday - Saturday:</strong>
                <br />
                9:00 AM - 6:00 PM
                <br />
                <strong>Sunday:</strong>
                <br />
                9:00 AM - 12:00 PM
              </p>
            </div>
          </div>
        </div>

        {/* Contact Form */}
        <form
          onSubmit={sendEmail}
          className="md:col-span-2 space-y-6 bg-white p-8"
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <div>
              <label
                htmlFor="name"
                className="block text-sm font-medium text-gray-700"
              >
                Your Name
              </label>
              <input
                type="text"
                name="name"
                id="name"
                value={formData.name}
                onChange={handleChange}
                className="mt-1 block w-full px-4 py-3 border border-gray-300 rounded-md shadow-sm focus:ring-primary-600 focus:border-primary-600"
                placeholder="Your Name"
                required
                aria-label="Your Name"
              />
            </div>
            <div>
              <label
                htmlFor="email"
                className="block text-sm font-medium text-gray-700"
              >
                Email Address
              </label>
              <input
                type="email"
                name="email"
                id="email"
                value={formData.email}
                onChange={handleChange}
                className="mt-1 block w-full px-4 py-3 border border-gray-300 rounded-md shadow-sm focus:ring-primary-600 focus:border-primary-600"
                placeholder="you@example.com"
                required
                aria-label="Email Address"
              />
            </div>
          </div>
          <div>
            <label
              htmlFor="subject"
              className="block text-sm font-medium text-gray-700"
            >
              Subject
            </label>
            <input
              type="text"
              name="subject"
              id="subject"
              value={formData.subject}
              onChange={handleChange}
              className="mt-1 block w-full px-4 py-3 border border-gray-300 rounded-md shadow-sm focus:ring-primary-600 focus:border-primary-600"
              placeholder="Subject"
              aria-label="Subject"
            />
          </div>
          <div>
            <label
              htmlFor="message"
              className="block text-sm font-medium text-gray-700"
            >
              Message
            </label>
            <textarea
              name="message"
              id="message"
              rows="6"
              value={formData.message}
              onChange={handleChange}
              className="mt-1 block w-full px-4 py-3 border border-gray-300 rounded-md shadow-sm focus:ring-primary-600 focus:border-primary-600"
              placeholder="Your message..."
              required
              aria-label="Message"
            ></textarea>
          </div>
          <div>
            <button
              type="submit"
              className={`w-full bg-primary-600 text-white px-6 py-3 rounded-md shadow-sm hover:bg-primary-700 transition duration-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-600 ${
                isSending ? "opacity-50 cursor-not-allowed" : ""
              }`}
              disabled={isSending}
              aria-label="Send Message"
            >
              {isSending ? "Sending..." : "Send Message"}
            </button>
          </div>
        </form>
      </div>
    </section>
  );
};

export default ContactSection;
